/**
 * OrgManagementAccessGuard
 * V139: org-management 통합 접근 가드
 * V142: org_manager → role_manager, role_exec 추가
 * V144: 로딩 상태 대기 개선 - auth_roleAssignments 조회 완료 전 접근 차단 방지
 * V29.2: role_admin_kr, role_admin_cn 추가 (관리자급 역할 통합)
 * V59: role_hr 단독 접근 불가 (조직장/임원/관리자만 허용)
 * V60: TL(jobTitleLevel) 조건 제거 - 역할 기반 접근만 허용
 *
 * 조직관리(/org-management/*) 전체 경로에 대한 접근 권한을 검증합니다.
 * 기존 OrgManagementRouteGuard(직책 레벨 정책)와 별개로,
 * 최소 접근 조건을 검증합니다.
 *
 * **접근 허용 조건 (OR):**
 * 1. userType === 'admin'
 * 2. 관리자급 역할 (system_admin, role_admin_kr, role_admin_cn) - V29.2 통합
 * 3. hasRole('role_manager') (V142: org_manager → role_manager)
 * 4. hasRole('role_exec') (V142: 임원 추가)
 *
 * **V60 변경사항:**
 * - TL(jobTitleLevel >= TEAM_LEADER) 조건 제거
 * - TL 직책만으로는 조직관리 접근 불가 (role_manager/role_exec 역할 필요)
 *
 * **V59 변경사항:**
 * - role_hr 단독 접근 불가 (기존: hasRole('role_hr') → 허용)
 * - role_hr + role_manager 조합은 role_manager 조건에서 허용됨
 *
 * 일반 팀원(TE) 및 TL(역할 없음)은 URL 직접 접근도 차단됩니다.
 */

import React, { useMemo } from 'react';
import { useAuthorization } from '../shared/hooks/useAuthorization';
import { useOrgManagementPolicy } from '../shared/hooks/useOrgManagementPolicy';
import { ADMIN_LIKE_ROLES } from '../types/role';

interface OrgManagementAccessGuardProps {
  children: React.ReactElement;
}

export function OrgManagementAccessGuard({ children }: OrgManagementAccessGuardProps) {
  const { user, hasRole, isLoading: authLoading } = useAuthorization();
  const { isLoading: policyLoading, isSystemAdmin } = useOrgManagementPolicy();

  // V142: user 로딩 중이면 먼저 대기 (hasRole이 정확히 동작하려면 user가 필요)
  // auth 로딩 완료 후에만 역할 기반 접근 판정
  const hasRoleBasedAccess = useMemo(() => {
    // user가 아직 로딩 중이면 판정 불가
    if (authLoading || !user) return null;

    // 1. userType === 'admin'
    const isAdminUser = (user?.profile as any)?.userType === 'admin';
    if (isAdminUser) return true;

    // 2. V29.2: 관리자급 역할 (system_admin, role_admin_kr, role_admin_cn)
    const hasAdminLikeRole = ADMIN_LIKE_ROLES.some(roleId => hasRole(roleId));
    if (hasAdminLikeRole) {
      return true;
    }

    // V59: role_hr 단독 허용 제거 (조직관리 메뉴 접근 불가)
    // 기존: if (hasRole('role_hr')) return true;
    // role_hr + role_manager 조합인 경우 아래 role_manager 조건에서 허용됨

    // 3. role_manager 역할 (V142: org_manager → role_manager)
    if (hasRole('role_manager')) return true;

    // 4. role_exec 역할 (V142: 임원 추가)
    if (hasRole('role_exec')) return true;

    return false;
  }, [user, hasRole, authLoading]);

  // V60: 접근 가능 여부 판정 (역할 기반만, TL 직책 조건 제거)
  const canAccess = useMemo(() => {
    // 역할 기반 접근 허용
    if (hasRoleBasedAccess === true) return true;

    // useOrgManagementPolicy에서 isSystemAdmin 확인 (관리자급 역할)
    if (isSystemAdmin) return true;

    // V60: TL(jobTitleLevel) 조건 제거
    // - TL 직책만으로는 조직관리 접근 불가
    // - role_manager 또는 role_exec 역할이 있어야 접근 가능

    return false;
  }, [hasRoleBasedAccess, isSystemAdmin]);

  // V141: 역할 기반 접근권한이 확정되면 즉시 통과
  if (hasRoleBasedAccess === true) {
    return children;
  }

  // V144: 로딩 중 (auth 또는 policy 로딩 중) - 반드시 로딩 완료 후 판정
  // hasRoleBasedAccess === null인 경우도 로딩 중으로 처리 (user가 아직 없음)
  if (authLoading || policyLoading || hasRoleBasedAccess === null) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        color: 'var(--color-gray-500)',
      }}>
        권한 확인 중...
      </div>
    );
  }

  // 접근 불가
  if (!canAccess) {
    console.warn(
      `[OrgManagementAccessGuard] V60 접근 차단: ` +
      `userType=${(user?.profile as any)?.userType}, ` +
      `isSystemAdmin=${isSystemAdmin}, ` +
      `hasRoleManager=${hasRole('role_manager')}, ` +
      `hasRoleExec=${hasRole('role_exec')}`
    );

    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '16px',
        }}>
          🔒
        </div>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '12px',
        }}>
          접근 권한이 없습니다
        </h2>
        <p style={{
          color: 'var(--color-gray-600)',
          marginBottom: '8px',
        }}>
          조직관리 메뉴는 조직장(role_manager) 또는 임원(role_exec) 권한이 필요합니다.
        </p>
        <p style={{
          color: 'var(--color-gray-500)',
          fontSize: '14px',
          marginBottom: '24px',
        }}>
          권한이 필요하시면 관리자에게 문의하세요.
        </p>
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '8px 24px',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              marginRight: '8px',
            }}
          >
            뒤로 가기
          </button>
          <button
            onClick={() => window.location.href = '/dashboard'}
            style={{
              padding: '8px 24px',
              background: 'var(--color-gray-200)',
              color: 'var(--color-gray-700)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            대시보드로 이동
          </button>
        </div>
      </div>
    );
  }

  // 접근 허용
  return children;
}
